import { useState } from 'react';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import { PaymentSubmit } from './payment-submit';
import { useUserLocation } from 'lib/hooks/useUserLocation';
import { QueryParam } from 'services/query-param/query-param';
import { useCustomNavigate } from 'lib/hooks/useCustomNavigate';
import * as Sentry from '@sentry/react';
import { sessionIdService } from 'services/session-id/session-id.service';
import { stagePathConfig } from 'data/stage';
import { paymentInitiationLocalStorageService } from 'services/localstorage/payment-initiation';
import { useTncApproval } from '../tnc-approval/useTncApproval';
import { AppToastService } from 'components/Toast/AppToastService';
import { scrollToElementById } from 'lib/helper/scroll/scroll';
import { useAppDispatch } from 'state/hooks';
import { setError } from 'state/slices/modal';

const PaymentSubmitStripe = () => {
  const stripe = useStripe();
  const elements = useElements();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const location = useUserLocation();
  const navigate = useCustomNavigate();
  const sessionId = sessionIdService.get();
  const dispatch = useAppDispatch();
  const { isApproved, isRequired } = useTncApproval();

  const mustApproveTnc = isRequired && !isApproved;

  const handleSubmit = async () => {
    if (!stripe || !elements) return;

    if (mustApproveTnc) {
      AppToastService.error('Please approve terms and conditions.');
      scrollToElementById('terms-and-conditions');
      return;
    }

    setIsLoading(true);

    const queryParams = QueryParam.getAllUnused();
    const stringQueryParams = QueryParam.stringify(queryParams);
    paymentInitiationLocalStorageService.set('initiated');
    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      redirect: 'if_required',
      confirmParams: {
        return_url: `${window.location.origin}${stagePathConfig.paymentProcessing}?${stringQueryParams}`,
        payment_method_data: {
          billing_details: {
            address: {
              // NOTE: this is needed since we disable the stripe element
              // dropdown to collect user country, see payment-method.tsx
              country: location?.country.code || 'AU',
            },
          },
        },
      },
    });

    // below is when on-session payments are happening, off-sessions
    // are redirected and don't reach this point
    if (
      error &&
      (error.type === 'card_error' ||
        error.type === 'validation_error' ||
        error.type === 'invalid_request_error')
    ) {
      dispatch(
        setError({
          isOpen: true,
          message:
            error.message ||
            'Your payment was unsuccessful. Please select a different payment method and try again.',
          buttonText: 'Return to payment',
        })
      );
      paymentInitiationLocalStorageService.remove();
    } else if (error) {
      Sentry.captureException('Payment failed unexpectedly.', {
        tags: {
          stripe: 'on-session payment error',
        },
        extra: {
          sessionId,
          message: error.message,
          type: error.type,
          paymentIntent: {
            id: error.payment_intent?.id,
            amount: error.payment_intent?.amount,
            currency: error.payment_intent?.currency,
          },
          paymentMethod: {
            type: error.payment_method?.type,
          },
        },
      });
      dispatch(
        setError({
          isOpen: true,
          message:
            error.message ||
            'Your payment was unsuccessful. Please select a different payment method and try again.',
          buttonText: 'Return to payment',
        })
      );
      paymentInitiationLocalStorageService.remove();
    }

    if (paymentIntent?.status === 'succeeded') {
      const params = {
        payment_intent: paymentIntent.id,
        payment_intent_client_secret: paymentIntent.client_secret ?? '',
        redirect_status: paymentIntent.status,
      };
      navigate(stagePathConfig.finished, { params });
    }

    setIsLoading(false);
  };

  return (
    <PaymentSubmit
      text="Agree & Finish"
      submit={handleSubmit}
      isLoading={isLoading}
      isDisabled={isLoading}
      buttonType={mustApproveTnc ? 'disabled' : 'primary'}
    />
  );
};

export { PaymentSubmitStripe };
